import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lightbulb, RefreshCw, Loader2, Check, X, ExternalLink,
  Code, Copy, ArrowRight, Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-blue-100 text-blue-800 border-blue-300",
  published: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function HuntedTips() {
  const [tips, setTips] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [sites, setSites] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);
  const [snippet, setSnippet] = useState(null);
  const [showSnippet, setShowSnippet] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/hunted-tips?status=${statusFilter}&limit=100`);
      setTips(r.data.tips || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/admin/hunted-portfolio/list`).then(r =>
      setSites(r.data.portals || []));
  }, []);

  async function updateStatus(id, status) {
    setActing(id);
    try {
      await axios.patch(`${API}/admin/hunted-tips/${id}`, { status });
      toast.success(`Status: ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setActing(null); }
  }

  async function convertToTopic(tipId, siteId) {
    setActing(tipId);
    try {
      const r = await axios.post(`${API}/admin/hunted-tips/${tipId}/convert-to-topic`, { site_id: siteId });
      toast.success(`Przekazano do agenta (${r.data.site})`);
      setAssignDialog(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setActing(null); }
  }

  async function loadSnippet() {
    try {
      const r = await axios.get(`${API}/admin/hunted-tips-widget-code`);
      setSnippet(r.data);
      setShowSnippet(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  function copySnippet() {
    if (!snippet?.snippet_html) return;
    navigator.clipboard.writeText(snippet.snippet_html);
    toast.success("Skopiowano do schowka!");
  }

  return (
    <div className="max-w-none p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-600" />
            Zgłoszenia tematów od czytelników
          </h1>
          <p className="text-slate-600 text-sm">
            Anonimowe zgłoszenia z hunted portali (widget "Zgłoś temat"). Zaakceptowane trafiają
            jako topics do agenta auto_publisher.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadSnippet}>
            <Code className="w-4 h-4 mr-1" />
            Kod widgetu (WordPress)
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Odśwież
          </Button>
        </div>
      </div>

      {/* Filtry statusu */}
      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "published", "rejected", "all"].map(s => (
          <Button key={s}
            variant={statusFilter === s ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(s)}
            className={statusFilter === s ? "bg-amber-600 hover:bg-amber-700" : ""}
          >
            {s === "all" ? "Wszystkie" : s === "pending" ? "Do przeglądu" :
              s === "approved" ? "Zaakceptowane" : s === "published" ? "Opublikowane" : "Odrzucone"}
            {counts[s] > 0 && <Badge variant="secondary" className="ml-2">{counts[s]}</Badge>}
          </Button>
        ))}
      </div>

      {loading && tips.length === 0 && (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </div>
      )}

      {tips.length === 0 && !loading && (
        <Card><CardContent className="py-8 text-center text-slate-500">
          Brak zgłoszeń w statusie "{statusFilter}".
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {tips.map(t => (
          <Card key={t.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg">{t.topic}</CardTitle>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    {t.portal_domain && (
                      <a href={`https://www.${t.portal_domain}`} target="_blank" rel="noopener"
                        className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                        {t.portal_domain} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <span>· {fmtDate(t.created_at)}</span>
                    {t.email && <span>· email: <strong>{t.email}</strong></span>}
                    {!t.email && <Badge variant="secondary" className="text-[10px]">anonim</Badge>}
                  </div>
                </div>
                <Badge className={STATUS_COLORS[t.status] || STATUS_COLORS.pending}>
                  {t.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.description}</p>
              {t.assigned_site_id && (
                <div className="mt-2 text-xs text-slate-500">
                  Przypisane do: <strong>{
                    sites.find(s => s.id === t.assigned_site_id)?.domain || t.assigned_site_id
                  }</strong>
                </div>
              )}
              {t.status === "pending" && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setAssignDialog(t)}
                    disabled={acting === t.id}>
                    {acting === t.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                    Przekaż do agenta
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => updateStatus(t.id, "approved")}
                    disabled={acting === t.id}>
                    <Check className="w-3 h-3 mr-1" /> Zaakceptuj (bez przekazania)
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                    onClick={() => updateStatus(t.id, "rejected")}
                    disabled={acting === t.id}>
                    <X className="w-3 h-3 mr-1" /> Odrzuć
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog: przypisz do portalu */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Do którego portalu przekazać temat?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <p className="text-sm text-slate-600 mb-2">
              Temat trafi do <code>agent_topic_queue</code> z priority=high i zostanie przetworzony
              w najbliższym cyklu auto_publisher.
            </p>
            {sites.map(s => (
              <button key={s.id}
                onClick={() => convertToTopic(assignDialog?.id, s.id)}
                disabled={acting === assignDialog?.id}
                className="w-full flex items-center gap-3 p-2 rounded border hover:bg-slate-50 text-left">
                {s.logo_url && <img src={s.logo_url} alt={s.domain} className="w-8 h-8 rounded" />}
                <div className="flex-1">
                  <div className="font-mono text-sm">{s.domain}</div>
                  <div className="text-xs text-slate-500">{s.niche}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: WordPress snippet */}
      <Dialog open={showSnippet} onOpenChange={setShowSnippet}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Kod widgetu do wklejenia na portale WordPress</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Skopiuj cały kod i wklej w <strong>footer.php</strong> motywu WordPress (przed <code>&lt;/body&gt;</code>)
              LUB w widget HTML (Wygląd → Widgety → HTML).
            </p>
            <div className="bg-slate-900 text-slate-100 rounded p-3 max-h-80 overflow-y-auto">
              <pre className="text-xs whitespace-pre-wrap">{snippet?.snippet_html?.substring(0, 3000)}...</pre>
            </div>
            <p className="text-xs text-slate-500">
              Backend URL: <code>{snippet?.backend_url}</code>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSnippet(false)}>Zamknij</Button>
            <Button onClick={copySnippet} className="bg-emerald-600 hover:bg-emerald-700">
              <Copy className="w-4 h-4 mr-1" /> Kopiuj cały snippet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
